import { useEffect, type ReactNode } from "react";
import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { Avatar } from "./display";
import { Button } from "./forms";

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(" ");

export type ThemeName = "light" | "dark";

/** Teams theme `default` → light; `dark` and `contrast` → dark (04 §9). The theme is an attribute, never `prefers-color-scheme`. */
export function ThemeProvider({ theme, children }: { theme: ThemeName; children: ReactNode }) {
  // dialogs, menus and tooltips are portalled to <body>, outside this element: the theme is also set on the document so their
  // text, fields and scrollbars take the dark tokens too (they showed light-theme greys on a dark surface)
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
    return () => root.removeAttribute("data-theme");
  }, [theme]);
  return (
    <FluentProvider
      theme={theme === "dark" ? webDarkTheme : webLightTheme}
      className="gp-root"
      data-theme={theme === "dark" ? "dark" : undefined}
      style={{ background: "transparent" }}
    >
      {children}
    </FluentProvider>
  );
}

/** Left panel full height, sibling of a column with the top bar and the content (04 AppShell). */
/** The GuestPilot logo (`/logo.png`, served by each app from its public folder). */
export function BrandMark({ size = 32 }: { size?: number }) {
  return <img className="gp-brand-mark" src="/logo.png" alt="" width={size} height={size} aria-hidden />;
}

export function AppShell({
  nav,
  topBar,
  children,
  tab,
}: {
  nav?: ReactNode;
  topBar?: ReactNode;
  children: ReactNode;
  tab?: boolean;
}) {
  return (
    <div className="gp-shell">
      {nav}
      <div className="gp-body-col">
        {topBar}
        <main className={cx("gp-main", tab && "gp-main--tab")} id="main">
          {children}
        </main>
      </div>
    </div>
  );
}

export function TopBar({ left, person }: { left: ReactNode; person?: { name: string } | undefined }) {
  return (
    <header className="gp-topbar">
      <div className="gp-row">{left}</div>
      {person ? <Avatar name={person.name} size={32} present /> : null}
    </header>
  );
}

export function Crumb({ root, page }: { root: string; page: string }) {
  return (
    <nav aria-label={root} className="gp-crumb t-body">
      <strong>{root}</strong> › <span aria-current="page">{page}</span>
    </nav>
  );
}

export interface NavItemSpec {
  path: string;
  label: string;
  icon: ReactNode;
  section?: string;
  /** Count shown after the label (unread notices); `badgeLabel` is what a screen reader says for it */
  badge?: number | undefined;
  badgeLabel?: string | undefined;
}

export function GpNav({
  brand,
  items,
  active,
  onNavigate,
  label,
  footer,
  skipLabel,
}: {
  brand: string;
  items: NavItemSpec[];
  active: string;
  onNavigate: (path: string) => void;
  label: string;
  footer?: ReactNode;
  skipLabel: string;
}) {
  const sections: { name: string | undefined; items: NavItemSpec[] }[] = [];
  for (const it of items) {
    const last = sections.at(-1);
    if (last && last.name === it.section) last.items.push(it);
    else sections.push({ name: it.section, items: [it] });
  }
  return (
    <nav className="gp-nav" aria-label={label}>
      <a className="gp-sr-only" href="#main">
        {skipLabel}
      </a>
      <div className="gp-nav-brand">
        <BrandMark />
        <span className="t-body-strong gp-nav-text">{brand}</span>
      </div>
      {sections.map((s, i) => (
        <div key={i} className="gp-stack" style={{ gap: "var(--space-2)" }}>
          {s.name ? <span className="gp-nav-label t-caption-strong gp-nav-text">{s.name}</span> : null}
          {s.items.map((it) => (
            <a
              key={it.path}
              href={it.path}
              className="gp-nav-item t-body"
              aria-current={
                active === it.path || (it.path !== "/" && active.startsWith(`${it.path}/`))
                  ? "page"
                  : undefined
              }
              title={it.label}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(it.path);
              }}
            >
              <span aria-hidden>{it.icon}</span>
              <span className="gp-nav-text">{it.label}</span>
              {it.badge ? (
                <span className="gp-nav-badge t-caption-strong" aria-label={it.badgeLabel}>
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              ) : null}
            </a>
          ))}
        </div>
      ))}
      {footer ? <div className="gp-nav-footer">{footer}</div> : null}
    </nav>
  );
}

export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="gp-page-head">
      <div>
        <h1 className="t-title-lg">{title}</h1>
        {subtitle ? <p className="t-body">{subtitle}</p> : null}
      </div>
      {actions ? <div className="gp-actions">{actions}</div> : null}
    </div>
  );
}

export type StepState = "done" | "next" | "later";

export interface Step {
  key: string;
  title: string;
  detail?: string | undefined;
  state: StepState;
  actionLabel?: string | undefined;
  laterLabel?: string | undefined;
  onAction?: (() => void) | undefined;
}

export function OnboardingStepList({ steps }: { steps: Step[] }) {
  return (
    <ol className="gp-steps">
      {steps.map((s, i) => (
        <li key={s.key} className="gp-step">
          <span className={cx("gp-glyph", s.state === "done" && "gp-glyph--done")} aria-hidden>
            {s.state === "done" ? "✓" : i + 1}
          </span>
          <div className="gp-step-body">
            <span className="t-body-strong">{s.title}</span>
            {s.detail ? <span className="t-body-sm gp-muted">{s.detail}</span> : null}
          </div>
          {s.state === "done" ? (
            <span className="gp-sr-only">{s.title}</span>
          ) : (
            <Button variant={s.state === "next" ? "primary" : "ghost"} onClick={s.onAction}>
              {s.state === "next" ? s.actionLabel : (s.laterLabel ?? s.actionLabel)}
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}

export interface MasterItem {
  id: string;
  label: string;
  sub?: string | undefined;
  state: "done" | "active" | "pending";
  outcome?: string | undefined;
}

/** Review master list: keyboard up/down moves the selection (04 MasterList). */
export function MasterList({
  label,
  items,
  selectedId,
  onSelect,
  doneLabel,
}: {
  label: string;
  items: MasterItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  doneLabel: string;
}) {
  return (
    <ul className="gp-masterlist" role="listbox" aria-label={label}>
      {items.map((it, i) => (
        <li key={it.id} role="presentation">
          <button
            type="button"
            role="option"
            className="gp-masteritem"
            aria-selected={it.id === selectedId}
            onClick={() => onSelect(it.id)}
            onKeyDown={(e) => {
              const d = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
              if (!d) return;
              e.preventDefault();
              const n = items[i + d];
              if (n) {
                onSelect(n.id);
                e.currentTarget.closest("ul")?.querySelectorAll<HTMLButtonElement>("button")[i + d]?.focus();
              }
            }}
          >
            <span
              className={cx(
                "gp-glyph",
                it.state === "done" && "gp-glyph--done",
                it.state === "active" && "gp-glyph--active",
              )}
              aria-hidden
            >
              {it.state === "done" ? "✓" : i + 1}
            </span>
            <span className="gp-person-text" style={{ flex: 1 }}>
              <span className="t-body-strong">{it.label}</span>
              {it.sub ? <span className="t-body-sm gp-faint">{it.sub}</span> : null}
            </span>
            <span className="t-body-sm gp-muted">{it.outcome ?? (it.state === "done" ? doneLabel : "")}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
